import { AnimationSequencesType } from '../../utility/constants';
import { getHTMLElement, isPromise } from '../../utility/helper';

export type AnimationStartsCallbacks = {
  onAnimationStarts?: (() => Promise<unknown>) | (() => void);
};

export type AnimationEndsCallbacks = {
  onAnimationEnds?: (() => Promise<unknown>) | (() => void);
};

export type AnimationStatusCallbacks = AnimationStartsCallbacks & AnimationEndsCallbacks;

// eslint-disable-next-line @typescript-eslint/ban-types
export type SequenceIdentifier = AnimationSequencesType | (string & {});

export interface NaviAnimation {
  animation: AnimationGeneratorType;
  elementSelector: string;
  onStart?: (() => Promise<unknown>) | (() => unknown);
  onEnd?: (() => Promise<unknown>) | (() => unknown);
}

export type SequenceStep = Array<NaviAnimation>;

export type AnimationSequence = {
  steps: Array<SequenceStep>;
  currentStepIndex: number;
  running: boolean;
  resolve?: (value: unknown) => void;
  reject?: (reason?: unknown) => void;
  promise?: Promise<void>;
};

export type AnimationGeneratorType = {
  className: string;
  cleanUpBeforeAnimation?: ((el: HTMLElement) => Promise<void>) | ((el: HTMLElement) => void) | boolean;
  cleanUpAfterAnimation?: ((el: HTMLElement) => Promise<void>) | ((el: HTMLElement) => void) | boolean;
};

const animationRegistry: Record<
  string,
  {
    promise: Promise<void>;
    resolve: (value: void) => void;
    reject: (reason?: unknown) => void;
    animation: AnimationGeneratorType;
    timeout: NodeJS.Timeout;
  }
> = {};

const sequences: {
  [key in SequenceIdentifier]?: AnimationSequence | undefined;
} = {};

let rootElement: HTMLElement | null = null;

const animationTimeoutDurationMs = 1000;

function initialize(element: HTMLElement): void {
  rootElement = element;

  rootElement.addEventListener('transitionend', animationEndListener);
  rootElement.addEventListener('animationend', animationEndListener);
}

function register(sequenceIdentifier: SequenceIdentifier, animationSequence: AnimationSequence): void {
  sequences[sequenceIdentifier] = animationSequence;
}

function reset(): void {
  Object.keys(sequences).forEach((key) => delete sequences[key]);
}

async function play(sequenceIdentifier: SequenceIdentifier): Promise<unknown> {
  const sequence = sequences[sequenceIdentifier];

  if (sequence === undefined) {
    return new Promise((_, reject) => reject(new Error(`Sequence ${sequenceIdentifier} has not been registered`)));
  }

  if (!sequence.running) {
    sequence.promise = new Promise<void>((resolve, reject) => {
      sequence.resolve = () => {
        sequence.running = false;
        resolve();
      };

      sequence.reject = (value: unknown) => {
        sequence.running = false;
        reject(value);
      };

      sequence.running = true;

      applyCurrentAnimationSequenceStep(sequence);
    });
  }

  return sequence.promise;
}

async function applyCurrentAnimationSequenceStep(sequence: AnimationSequence) {
  if (sequence.currentStepIndex >= sequence.steps.length) {
    finishAnimationSequence(sequence);
    return;
  }

  const sequenceStep = sequence.steps[sequence.currentStepIndex];

  const promises = [];
  for (const animation of sequenceStep) {
    promises.push(executeAnimation(animation));
  }

  try {
    await Promise.all(promises);
  } catch (error) {
    sequence.reject?.(error);
    return;
  }

  sequence.currentStepIndex += 1;

  setTimeout(() => applyCurrentAnimationSequenceStep(sequence), 0);
}

function finishAnimationSequence(sequence: AnimationSequence) {
  sequence.currentStepIndex = 0;
  sequence.resolve?.(null);
  sequence.promise = undefined;
}

function animationEndListener(event: AnimationEvent | TransitionEvent): void {
  const target = getHTMLElement(event.target);

  if (target === null) {
    throw new Error(`${event.type} event target is null`);
  }

  const id = target.dataset.phnAmId;

  if (id !== undefined && id !== null) {
    finishAnimationPromise(id, target);
  }
}

async function executeAnimation({ onStart, onEnd, animation, elementSelector }: NaviAnimation): Promise<void> {
  if (rootElement === null) {
    throw new Error('No root element present');
  }

  if (onStart) {
    const maybePromise = onStart();
    if (isPromise<void>(maybePromise)) {
      await maybePromise;
    }
  }

  const animatedElements: HTMLElement[] = Array.from(rootElement.querySelectorAll(elementSelector));
  const animationExecutors = [];

  for (const element of animatedElements) {
    const animationPromiseEffect = generateAndCleanUpAnimationPromise(animationPromiseGenerator)(element, animation);
    animationExecutors.push(animationPromiseEffect);
  }

  await Promise.all(animationExecutors);

  if (onEnd) {
    const maybePromise = onEnd();
    if (isPromise<void>(maybePromise)) {
      await maybePromise;
    }
  }
}

function generateAndCleanUpAnimationPromise(
  animationPromiseGenerator: (element: HTMLElement, animation: AnimationGeneratorType) => Promise<void>
): (element: HTMLElement, animation: AnimationGeneratorType) => Promise<void> {
  return async function (element: HTMLElement, animation: AnimationGeneratorType): Promise<void> {
    const { cleanUpBeforeAnimation, cleanUpAfterAnimation } = animation;

    const animationPromise = animationPromiseGenerator(element, animation);

    if (typeof cleanUpBeforeAnimation === 'function') {
      const maybePromise: void | Promise<void> = cleanUpBeforeAnimation(element);
      if (isPromise<void>(maybePromise)) {
        await maybePromise;
      }
    }

    await animationPromise;

    if (typeof cleanUpAfterAnimation === 'function') {
      const maybePromise: void | Promise<void> = cleanUpAfterAnimation(element);
      if (isPromise<void>(maybePromise)) {
        await maybePromise;
      }
    }

    if (cleanUpAfterAnimation !== false) {
      // only not remove class if cleanUpAfterAnimation is explicitely set to false
      element.classList.remove(animation.className);
    }
  };
}

const animationPromiseGenerator = (element: HTMLElement, animation: AnimationGeneratorType): Promise<void> => {
  const id = Math.floor(Math.random() * 1e7);

  element.classList.add(animation.className);
  element.dataset.phnAmId = id.toString();

  let resolve!: (value: void) => void;
  let reject!: (reason?: unknown) => void;
  let timeout!: NodeJS.Timeout;
  const promise = new Promise<void>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;

    timeout = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn(
        `Animation with className ${animation.className} didn't finish after ${animationTimeoutDurationMs}ms. Forcefully finishing animation and continuing with the rest of the sequence.`
      );
      finishAnimationPromise(String(id), element);
    }, animationTimeoutDurationMs);
  });

  animationRegistry[id] = { animation, resolve, reject, promise, timeout };

  return promise;
};

function finishAnimationPromise(phnAmId: string, target: HTMLElement): void {
  const { resolve, timeout } = animationRegistry[phnAmId];

  clearTimeout(timeout);
  delete animationRegistry[phnAmId];
  delete target.dataset.phnAmId;

  resolve();
}

function isPlaying(sequenceIdentifier: SequenceIdentifier): boolean {
  const sequence = sequences[sequenceIdentifier];

  if (sequence === undefined) {
    throw new Error(`Sequence ${sequenceIdentifier} has not been registered`);
  }

  return sequence.promise !== undefined;
}

function makeSequence(
  sequenceSteps: SequenceStep[],
  animationDefaults?: Partial<Omit<AnimationGeneratorType, 'name'>>
): AnimationSequence {
  return {
    steps: sequenceSteps.map((step) => step.map((animation) => ({ ...animationDefaults, ...animation }))),
    currentStepIndex: 0,
    running: false
  };
}

export default {
  initialize,
  play,
  isPlaying,
  register,
  reset
};

export { makeSequence };
